import React, { useContext, useEffect, useState } from "react";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import { api, download } from "./hctc_common";
import "./hctc.css";

// Historical case reports (screen hist_reports), spec 21. Exports to PDF, Word and Excel.
const EMPTY = {
  divisionId: "", courtTypeId: "", caseTypeId: "", filingYearFrom: "", filingYearTo: "", verificationStatus: "",
  digitizationStatus: "", availabilityStatus: "", applicationStatus: "", fromDate: "", toDate: "",
};

export default function HistoricalReports() {
  const { token, frequentData, setIsExpired } = useContext(ShareContext);
  const [lk, setLk] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const body = (x) => ({
    ...x,
    divisionId: x.divisionId ? Number(x.divisionId) : null,
    courtTypeId: x.courtTypeId ? Number(x.courtTypeId) : null,
    caseTypeId: x.caseTypeId ? Number(x.caseTypeId) : null,
    filingYearFrom: x.filingYearFrom ? Number(x.filingYearFrom) : null,
    filingYearTo: x.filingYearTo ? Number(x.filingYearTo) : null,
    fromDate: x.fromDate || null,
    toDate: x.toDate || null,
    verificationStatus: x.verificationStatus || null,
    digitizationStatus: x.digitizationStatus || null,
    availabilityStatus: x.availabilityStatus || null,
    applicationStatus: x.applicationStatus || null,
  });

  const run = async (x) => {
    setErr("");
    setLoading(true);
    const d = await api(token, setIsExpired, "historical/report", { method: "POST", body: body(x) });
    setLoading(false);
    if (d.code === 0) setRep(d.report);
    else if (d.code !== -10) setErr(d.status || "Unable to run the report");
  };

  useEffect(() => {
    (async () => {
      const [l, c] = await Promise.all([api(token, setIsExpired, "historical/lookups"), api(token, setIsExpired, "serviceapp/config")]);
      if (l.code === 0) setLk(l);
      if (c.code === 0) setStatuses(c.statuses || []);
    })();
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const on = (e) => setF({ ...f, [e.target.name]: e.target.value });

  const onExport = async (doctype, list) => {
    const ext = { excel: "xlsx", msword: "docx", pdf: "pdf" }[doctype];
    setLoading(true);
    try {
      await download(token, setIsExpired, `historical/${list ? "list-export" : "report-export"}/${doctype}`, `historical-${list ? "cases" : "report"}.${ext}`, {
        method: "POST",
        body: body(f),
      });
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  const sel = (name, label, options, keyed) => (
    <div className="hctc-field">
      <label>{label}</label>
      <select name={name} value={f[name]} onChange={on}>
        <option value="">All</option>
        {(options || []).map((o) =>
          keyed ? (
            <option key={o.id} value={o.id}>
              {o.title || o.caseType1}
            </option>
          ) : (
            <option key={o}>{o}</option>
          ),
        )}
      </select>
    </div>
  );

  const s = rep?.summary;
  const stat = (n, l) => (
    <div className="hctc-stat">
      <div className="n">{n ?? "-"}</div>
      <div className="l">{l}</div>
    </div>
  );
  const table = (title, rows, cols) => (
    <div className="hctc-card">
      <div className="head">
        <h3>{title}</h3>
      </div>
      <table className="case-table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c[0]}>{c[1]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c[0]}>{r[c[0]]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(!rows || rows.length === 0) && <div className="hctc-muted">No data</div>}
    </div>
  );

  return (
    <div className="hctc-page">
      <h2>Historical Case Reports</h2>
      <div className="hctc-sub">Registration, verification, digitization and historical service applications.</div>
      <div className="hctc-card">
        <div className="hctc-grid">
          {sel("divisionId", "Judicial division", frequentData?.divisions, true)}
          {sel("courtTypeId", "Court", lk?.courts, true)}
          {sel("caseTypeId", "Case type", frequentData?.casetype, true)}
          <div className="hctc-field">
            <label>Filed from year</label>
            <input type="number" name="filingYearFrom" value={f.filingYearFrom} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>Filed to year</label>
            <input type="number" name="filingYearTo" value={f.filingYearTo} onChange={on} />
          </div>
          {sel("verificationStatus", "Verification status", lk?.verificationStatuses)}
          {sel("digitizationStatus", "Digitization status", lk?.digitizationStatuses)}
          {sel("availabilityStatus", "Availability status", lk?.availabilityStatuses)}
          {sel("applicationStatus", "Application status", statuses)}
          <div className="hctc-field">
            <label>Recorded from</label>
            <input type="date" name="fromDate" value={f.fromDate} onChange={on} />
          </div>
          <div className="hctc-field">
            <label>Recorded to</label>
            <input type="date" name="toDate" value={f.toDate} onChange={on} />
          </div>
        </div>
        <div className="hctc-actions" style={{ marginTop: 12 }}>
          <JisButton onClick={() => run(f)}>Generate</JisButton>
          <JisButton variant="outline" onClick={() => onExport("pdf")}>
            Summary PDF
          </JisButton>
          <JisButton variant="outline" onClick={() => onExport("msword")}>
            Summary Word
          </JisButton>
          <JisButton variant="outline" onClick={() => onExport("excel")}>
            Summary Excel
          </JisButton>
          <JisButton variant="plain" onClick={() => onExport("excel", true)}>
            Case list (Excel)
          </JisButton>
        </div>
      </div>
      {err && <div className="hctc-err">{err}</div>}
      {s && (
        <>
          <div className="hctc-stats">
            {stat(s.registered, "Historical cases registered")}
            {stat(s.verified, "Verified")}
            {stat(s.pendingVerification, "Pending verification")}
            {stat(s.digitized, "Fully digitized")}
            {stat(s.partiallyDigitized, "Partially digitized")}
            {stat(s.missingRecords, "With missing records")}
            {stat(s.recordsNotFound, "Records not found")}
            {stat(s.linkedToJis, "Linked to JIS")}
            {stat(s.duplicates, "Duplicates")}
          </div>
          <div className="hctc-stats">
            {stat(s.ctcRequests, "Historical CTC requests")}
            {stat(s.decreeApplications, "Historical decree applications")}
            {stat(s.defaultFeeApplications, "Historical default fee applications")}
            {stat(s.caseSearchRequests, "Historical case searches")}
            {stat(s.averageRetrievalDays, "Avg retrieval time (days)")}
            {stat(s.averageDigitizationDays, "Avg digitization time (days)")}
          </div>
          <div className="hctc-grid wide">
            {table("Cases by division", rep.byDivision, [["key", "Division"], ["cases", "Cases"], ["verified", "Verified"], ["digitized", "Digitized"]])}
            {table("Cases by filing year", rep.byYear, [["key", "Year"], ["cases", "Cases"], ["verified", "Verified"], ["digitized", "Digitized"]])}
            {table("Applications by service", rep.appsByService, [["key", "Service"], ["applications", "Applications"], ["completed", "Completed"]])}
            {table("Applications by division", rep.appsByDivision, [["key", "Division"], ["applications", "Applications"]])}
            {table("Applications by filing year", rep.appsByYear, [["key", "Year"], ["applications", "Applications"]])}
          </div>
        </>
      )}
      <BackLoader open={loading} />
    </div>
  );
}
